import { FC } from 'react'
import { Box, ColumnLayout, SpaceBetween } from '@cloudscape-design/components'
import useSingleTags from '../../../hooks/query/useSingleTags'
import LoadingSpinner from '../../LoadingSpinner'
import ApproverOverview from '../ApproverOverview'
import { keysToLabel } from '../../../helper/toCamelCase'
import {
	getApplicationApprovals,
	getProjectLevelApprovals
} from '../../../helper/applicationTagApproval'
import type { PlatformOwnerResource } from '../../../helper/platformOwnerPermissions'

interface ReadGenericTagsProps {
	tagsId: string
	layout?: number
	showApprovalStatus?: boolean
	allowPlatformOwnerApproval?: boolean
	applicationName?: string
	platformOwnerResource?: PlatformOwnerResource
	platformOwnerEmail?: string
	applicationOwnerEmail?: string
}

const ReadGenericTagsGCP: FC<ReadGenericTagsProps> = ({
	tagsId,
	layout = 2,
	showApprovalStatus = true,
	allowPlatformOwnerApproval = false,
	applicationName,
	platformOwnerResource,
	platformOwnerEmail,
	applicationOwnerEmail
}) => {
	const { data: originalTags, isLoading: isLoadingTags } = useSingleTags(tagsId)

	if (!originalTags || isLoadingTags) return <LoadingSpinner />

    // "applications" is the per-application registry on a shared GCP Project tag record,
    // not a tag value - it is rendered on each application request instead.
    const excludedKeys = ["tagProject", "dataClassification", "applications", "fundingChoice", "parentTagsID"];
    const tagKeys = Object.keys(originalTags['details']).filter(
        (key) => !excludedKeys.includes(key)
    )
	const getLabel = (tagkey: string) => {
		switch (tagkey) {
			case 'tagProject':
				return 'Project'
			case 'WBSCode':
			case 'W B S Code':
				return 'WBS Code'
			default:
				return keysToLabel(tagkey)
		}
	}
	return (
		<>
			<SpaceBetween direction="vertical" size="s">
				<ColumnLayout columns={layout} variant="text-grid">
					{tagKeys?.map((tagkey) => (
						<div key={tagkey}>
							<Box variant="awsui-key-label">{getLabel(tagkey)}</Box>
							{typeof originalTags['details'][tagkey] === 'object'
								? originalTags['details'][tagkey]['value']
								: originalTags['details'][tagkey] === ''
								? 'NA'
								: originalTags['details'][tagkey]}
						</div>
					))}
				</ColumnLayout>
				{showApprovalStatus && (
					// Project views show project approvals; application views pass their name
					// to show only that application's approvals on the shared tag record.
					<ApproverOverview
						approvalStatus={applicationName
							? getApplicationApprovals(originalTags['approvalStatus'], applicationName)
							: getProjectLevelApprovals(originalTags['approvalStatus'])}
						isDisabled={true}
						allowPlatformOwnerApproval={allowPlatformOwnerApproval}
						platformOwnerResource={platformOwnerResource}
						tagsID={tagsId}
						tagDetails={{
							...originalTags.details,
							platformOwnerEmail:
								platformOwnerEmail || originalTags.details?.platformOwnerEmail,
							applicationOwnerEmail:
								applicationOwnerEmail ||
								originalTags.details?.applicationOwnerEmail
						}}
					/>
				)}
			</SpaceBetween>
		</>
	)
}

export default ReadGenericTagsGCP
